(() => {
  'use strict';
  const configElement = document.getElementById('ufm-compare-config');
  if (!configElement) return;
  let config;
  try { config = JSON.parse(configElement.textContent); } catch (_) { return; }
  const key = `ufm:compare:v1:${config.customer}`;
  const account = window.UFMSavedLists;
  const accountMode = Boolean(account?.enabled);
  const migrationKey = `ufm:compare:migrated:v1:${config.customer}`;
  const page = document.querySelector('[data-ufm-compare-page]');
  const notice = document.querySelector('[data-ufm-compare-notice]');
  if (notice) notice.textContent = accountMode ? 'Saving comparisons to your account requires server confirmation.' : 'Compare account integration is unavailable. No products can be saved.';
  const unavailable = 'Not saved. Compare account integration is unavailable. Please try again after the account service is configured.';
  let importButton = document.querySelector('[data-ufm-compare-import]');
  if (accountMode && config.customer && page && !importButton) {
    importButton = document.createElement('button');
    importButton.type = 'button';
    importButton.setAttribute('data-ufm-compare-import', '');
    importButton.textContent = 'Import browser comparison';
    page.append(importButton);
  }
  if (importButton) importButton.hidden = true;
  let imported = false;
  const feedback = document.createElement('p');
  feedback.className = 'ufm-compare-feedback';
  feedback.setAttribute('role', 'status');
  feedback.hidden = true;
  document.body.append(feedback);
  const shortcut = document.createElement('a');
  shortcut.className = 'ufm-compare-shortcut';
  shortcut.href = config.page;
  shortcut.hidden = true;
  document.body.append(shortcut);
  const cache = new Map();
  const originalLabels = new WeakMap();
  const basketState = new Map();
  let renderVersion = 0;

  function message(text) {
    const status = page?.querySelector('[data-ufm-compare-message]') || document.querySelector('.ufm-product-page__saved-action-status') || feedback;
    status.textContent = text;
    status.hidden = false;
  }

  function identity(value) {
    if (typeof value !== 'string' && !(typeof value === 'number' && Number.isSafeInteger(value))) return null;
    const text = String(value);
    if (!/^\d+$/.test(text)) return null;
    const id = text.replace(/^0+/, '');
    return id || null;
  }

  function normalizeEntry(entry) {
    const id = identity(entry?.id);
    const variant = identity(entry?.variant);
    return id && variant && typeof entry.url === 'string' && entry.url ? { ...entry, id, variant } : null;
  }

  function read() {
    if (!config.customer) return [];
    if (!accountMode) throw new Error(unavailable);
    if (accountMode) {
      const entries = account.read('compare');
      if (!Array.isArray(entries)) throw new Error('Account comparison list is unavailable.');
      const normalized = entries.map(normalizeEntry);
      if (normalized.some((entry) => !entry)) throw new Error('Account comparison list is unavailable.');
      return normalized.filter((entry, index) => normalized.findIndex((item) => item.id === entry.id) === index);
    }
  }

  async function refresh() {
    if (!accountMode) {
      document.querySelectorAll('[data-ufm-compare]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      return;
    }
    let entries;
    try {
      if (accountMode && config.customer) await account.ready;
      entries = read();
    } catch (error) { message(`Account comparison list is unavailable. ${error.message}`); return; }
    document.querySelectorAll('[data-ufm-compare]').forEach((button) => {
      const added = entries.some((entry) => entry.id === identity(button.dataset.productId));
      if (!originalLabels.has(button)) originalLabels.set(button, button.getAttribute('aria-label'));
      const title = button.dataset.productTitle || originalLabels.get(button) || 'product';
      button.setAttribute('aria-pressed', String(added));
      button.setAttribute('aria-label', added ? `Added to Compare. Click to remove ${title}.` : `Add ${title} to Compare`);
      const label = button.querySelector('[data-ufm-compare-label]');
      if (label) label.textContent = added ? '\u2713 Added to Compare' : 'Compare';
    });
    shortcut.textContent = `View comparison (${entries.length})`;
    shortcut.hidden = !entries.length || Boolean(page);
    if (accountMode && config.customer && importButton) {
      try {
        const browserEntries = JSON.parse(localStorage.getItem(key) || '[]');
        importButton.hidden = imported || Boolean(localStorage.getItem(migrationKey)) || !Array.isArray(browserEntries) || !browserEntries.length;
      } catch (error) { importButton.hidden = true; message(`Browser comparison import is unavailable. ${error.message}`); }
    }
  }

  async function mutate(action, payload) {
    if (!accountMode) throw new Error(unavailable);
    if (!config.customer) throw new Error('Log in to compare products.');
    await account.ready;
    await account.mutate('compare', action, payload);
    const entries = read();
    // Merge deduplicates by product ID and preserves variants already in the account.
    const acknowledged = action === 'add' ? entries.some((entry) => entry.id === payload.entry.id && entry.variant === payload.entry.variant) :
      action === 'remove' ? !entries.some((entry) => entry.id === payload.id) :
      action === 'clear' ? !entries.length :
      action === 'merge' && payload.entries.every((requested) => entries.some((entry) => entry.id === requested.id));
    if (!acknowledged) throw new Error('Account comparison update was not confirmed. Please retry.');
    await refresh();
    if (page) await render();
  }

  async function removeProduct(id) {
    await mutate('remove', { id });
    message('Product removed from Compare.');
  }

  function login(returnUrl) {
    const url = new URL(config.login, location.origin);
    url.searchParams.set('return_to', returnUrl.pathname + returnUrl.search + returnUrl.hash);
    location.assign(url.href);
  }

  function productUrl(path) {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) {
      throw new Error('Invalid comparison product URL.');
    }
    return url;
  }

  async function data(entry) {
    const url = productUrl(entry.url);
    url.search = '';
    url.searchParams.set('view', 'ufm-compare-data');
    if (!cache.has(url.href) || cache.get(url.href).expiresAt <= Date.now()) {
      const request = (async () => {
        const response = await fetch(url.href, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Product information is unavailable.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const payload = html.querySelector('[data-ufm-compare-data]');
        if (!payload) throw new Error('Comparison product data is unavailable.');
        const product = JSON.parse(payload.textContent);
        if (!product || typeof product.url !== 'string') throw new Error('Invalid comparison product data.');
        const canonical = productUrl(product.url);
        if (canonical.pathname.replace(/\/$/, '') !== url.pathname.replace(/\/$/, '')) {
          throw new Error('Comparison product URL does not match.');
        }
        const validSpecs = (rows) => rows == null || (Array.isArray(rows) && rows.every((row) => row && typeof row.key === 'string'));
        if (typeof product.title !== 'string' || !product.title.trim() || !Array.isArray(product.variants) ||
          !validSpecs(product.specs) || product.variants.some((variant) => !variant || !identity(variant.id) ||
            typeof variant.title !== 'string' || !validSpecs(variant.specs))) {
          throw new Error('Invalid comparison product data.');
        }
        return product;
      })();
      const cached = { request, expiresAt: Infinity };
      cache.set(url.href, cached);
      request.then(() => { cached.expiresAt = Date.now() + 60000; }, () => {});
      request.catch(() => cache.delete(url.href));
    }
    const product = await cache.get(url.href).request;
    if (identity(product.id) !== entry.id) throw new Error('Comparison product identity does not match.');
    const variant = product.variants.find((item) => identity(item.id) === entry.variant);
    if (!variant) throw new Error('The saved variant is no longer available. Remove this product and choose a current variant.');
    return { product, variant, entry };
  }

  async function add(entry) {
    if (!accountMode) throw new Error(unavailable);
    if (!config.customer) throw new Error('Log in to compare products.');
    if (accountMode) await account.ready;
    entry = normalizeEntry(entry);
    if (!entry) throw new Error('Invalid comparison product or variant ID.');
    const resolved = await data(entry);
    entry.url = productUrl(resolved.product.url).pathname;
    await mutate('add', { entry });
    message('Product added to Compare. Saved to your account.');
  }

  function format(value) {
    if (value == null) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.map(format).filter(Boolean).join(', ');
    if (typeof value === 'object') {
      if ('value' in value) return `${format(value.value)}${value.unit ? ` ${value.unit}` : ''}`;
      if ('amount' in value) return `${value.amount}${value.currency_code ? ` ${value.currency_code}` : ''}`;
      if ('color' in value) return format(value.color);
      if ('rating' in value) return `${value.rating}${value.scale_max ? ` / ${value.scale_max}` : ''}`;
      return '';
    }
    return String(value).trim();
  }

  function specifications({ product, variant }) {
    const rows = new Map();
    const put = (label, value) => {
      const text = format(value);
      const normalized = String(label).trim().toLowerCase().replace(/[_\s-]+/g, ' ');
      if (normalized && text && !rows.has(normalized)) rows.set(normalized, { label: String(label).replace(/_/g, ' '), value: text });
    };
    put('SKU', variant.sku);
    put('Price', variant.price);
    put('Stock status', variant.available === true ? 'In stock' : variant.available === false ? 'Sold out' : 'Unknown');
    put('Brand', product.vendor);
    if (variant.title !== 'Default Title') put('Variant', variant.title);
    [...(variant.specs || []), ...(product.specs || [])].forEach((row) => put(row.key, row.value));
    if (Array.isArray(product.custom_rows)) product.custom_rows.forEach((row) => {
      if (Array.isArray(row) && row.length >= 2) put(row[0], row[1]);
    });
    return rows;
  }

  function element(tag, text, className) {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    if (className) node.className = className;
    return node;
  }

  async function basket(button, item) {
    if (button.disabled || basketState.has(item.variant.id)) return;
    basketState.set(item.variant.id, 'pending');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    let added = false;
    try {
      const sections = cart ? cart.getSectionsToRender().map((section) => section.id) : [];
      cart?.setActiveElement(button);
      const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: item.variant.id, quantity: item.variant.quantity_rule?.min || 1 }],
          ...(sections.length ? { sections, sections_url: location.pathname } : {}) })
      });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || state.message || 'Unable to add this product to your basket.');
      added = true;
      if (cart) {
        if (sections.some((id) => !state.sections?.[id])) throw new Error('Basket preview unavailable');
        cart.classList.remove('is-empty');
        cart.renderContents({ ...state, key: state.items?.[0]?.key, id: state.items?.[0]?.id });
        message('Product added to your basket.');
      } else location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ufm-compare', productVariantId: item.variant.id, cartData: state });
      }
    } catch (error) {
      message(added ? 'Product added, but the basket preview could not refresh. View your basket before adding again.' :
        `${error.message} If the connection was interrupted, check your basket before retrying.`);
      const link = element('a', 'View basket');
      link.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
      page.querySelector('[data-ufm-compare-message]').append(' ', link);
    } finally {
      if (added) basketState.set(item.variant.id, 'added');
      else basketState.delete(item.variant.id);
      button.removeAttribute('aria-busy');
      button.disabled = added;
      if (added) button.textContent = 'Added to Basket';
      if (!button.isConnected) render();
    }
  }

  async function render() {
    const version = ++renderVersion;
    const container = page.querySelector('[data-ufm-compare-table]');
    const clear = page.querySelector('[data-ufm-compare-clear]');
    if (!accountMode) {
      clear.disabled = true;
      container.replaceChildren(element('p', unavailable));
      return;
    }
    let entries;
    try {
      if (accountMode && config.customer) {
        clear.disabled = true;
        container.replaceChildren(element('p', 'Loading account comparison...'));
        await account.ready;
        if (version !== renderVersion) return;
      }
      entries = read();
    } catch (error) {
      if (version !== renderVersion) return;
      clear.disabled = true;
      container.replaceChildren(element('p', 'Account comparison list is unavailable. Please retry when the service is available.'));
      message(`Account comparison list is unavailable. ${error.message}`);
      return;
    }
    clear.disabled = !entries.length;
    if (!config.customer) {
      const link = element('a', 'Log in to compare products');
      link.href = config.login;
      link.addEventListener('click', (event) => { event.preventDefault(); login(new URL(location.href)); });
      container.replaceChildren(link);
      return;
    }
    if (!entries.length) { container.replaceChildren(element('p', 'Your comparison list is empty. Add products using Compare.')); return; }
    container.replaceChildren(element('p', 'Loading current product information...'));
    const results = await Promise.all(entries.map(async (entry) => {
      try { return await data(entry); } catch (error) { return { entry, error: error.message }; }
    }));
    if (version !== renderVersion) return;
    const table = element('table', null, 'ufm-compare-table');
    const caption = element('caption', 'Product comparison');
    table.append(caption);
    const head = element('thead');
    const titles = element('tr');
    const heading = element('th', 'Product');
    heading.scope = 'col';
    titles.append(heading);
    const specs = results.map((item) => item.error ? new Map() : specifications(item));
    results.forEach((item) => {
      const cell = element('th');
      cell.scope = 'col';
      if (item.error) cell.append(element('p', item.error));
      else {
        if (item.variant.image) {
          const image = element('img');
          image.src = item.variant.image;
          image.alt = item.product.title;
          image.width = 240;
          image.height = 180;
          image.loading = 'lazy';
          cell.append(image);
        }
        cell.append(element('h2', item.product.title));
        const view = element('a', 'View product');
        const url = productUrl(item.product.url);
        url.searchParams.set('variant', item.variant.id);
        view.href = url.href;
        cell.append(view);
        if (!item.product.requires_selling_plan) {
          const state = basketState.get(item.variant.id);
          const label = state === 'added' ? 'Added to Basket' : state === 'pending' ? 'Adding...' : item.variant.available ? 'Add to Basket' : 'Sold out';
          const cartButton = element('button', label, 'button button--secondary');
          cartButton.type = 'button';
          cartButton.disabled = !item.variant.available || Boolean(state);
          cartButton.addEventListener('click', () => basket(cartButton, item));
          cell.append(cartButton);
        } else cell.append(element('p', 'Choose purchase options on the product page.'));
      }
      const remove = element('button', 'Remove product');
      remove.type = 'button';
      remove.addEventListener('click', async () => {
        remove.disabled = true;
        try { await removeProduct(item.entry.id); }
        catch (error) { message(error.message); }
        finally { remove.disabled = false; }
      });
      cell.append(remove);
      titles.append(cell);
    });
    head.append(titles);
    table.append(head);
    const body = element('tbody');
    const labels = new Map();
    specs.forEach((rows) => rows.forEach((row, name) => { if (!labels.has(name)) labels.set(name, row.label); }));
    labels.forEach((label, name) => {
      const row = element('tr');
      const th = element('th', label);
      th.scope = 'row';
      row.append(th);
      specs.forEach((rows) => row.append(element('td', rows.get(name)?.value || '')));
      body.append(row);
    });
    table.append(body);
    if (entries.length < 2) container.replaceChildren(element('p', 'Add at least 2 products to compare.'), table);
    else container.replaceChildren(table);
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ufm-compare]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    if (button.dataset.compareBusy === 'true') return;
    if (!accountMode) { message(unavailable); return; }
    let entry = { id: button.dataset.productId, variant: button.dataset.variantId, url: button.dataset.productUrl };
    if (button.hasAttribute('data-ufm-compare-product-page')) {
      const section = button.closest('.shopify-section');
      entry.variant = section?.querySelector('form[action*="/cart/add"] [name="id"]')?.value || entry.variant;
    }
    entry = normalizeEntry(entry);
    if (!entry) { message('Invalid comparison product or variant ID.'); return; }
    try { productUrl(entry.url); } catch (error) { message(error.message); return; }
    if (!config.customer) {
      const returnUrl = new URL(location.href);
      returnUrl.searchParams.set('ufm_compare_product', entry.id);
      returnUrl.searchParams.set('ufm_compare_variant', entry.variant);
      returnUrl.searchParams.set('ufm_compare_url', entry.url);
      login(returnUrl);
      return;
    }
    button.dataset.compareBusy = 'true';
    button.setAttribute('aria-busy', 'true');
    try {
      if (accountMode) await account.ready;
      const entries = read();
      if (entries.some((item) => item.id === entry.id)) {
        await removeProduct(entry.id);
      } else await add(entry);
    } catch (error) { message(error.message); }
    finally { delete button.dataset.compareBusy; button.removeAttribute('aria-busy'); }
  }, true);

  document.addEventListener('keydown', (event) => {
    const button = event.target.closest('a[data-ufm-compare]');
    if (button && event.key === ' ') { event.preventDefault(); button.click(); }
  });

  if (page) {
    const clear = page.querySelector('[data-ufm-compare-clear]');
    clear.addEventListener('click', async () => {
      if (accountMode) clear.disabled = true;
      try {
        await mutate('clear', {});
        message('Comparison cleared.');
      } catch (error) { message(error.message); }
      finally { if (accountMode) { try { clear.disabled = !read().length; } catch (_) { clear.disabled = true; } } }
    });
    render();
  }
  if (accountMode && config.customer && importButton) importButton.addEventListener('click', async () => {
    if (importButton.disabled || imported) return;
    importButton.disabled = true;
    try {
      await account.ready;
      if (localStorage.getItem(migrationKey)) { importButton.hidden = true; return; }
      if (!window.confirm('Import your browser comparison list into your account? The original browser list will be retained.')) return;
      const saved = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(saved)) throw new Error('Invalid browser comparison list.');
      if (!saved.length) { importButton.hidden = true; return; }
      const entries = saved.map(normalizeEntry);
      if (entries.some((entry) => !entry)) throw new Error('Invalid comparison product or variant ID.');
      const unique = entries.filter((entry, index) => entries.findIndex((item) => item.id === entry.id) === index);
      for (const entry of unique) {
        const resolved = await data(entry);
        entry.url = productUrl(resolved.product.url).pathname;
      }
      await mutate('merge', { entries: unique });
      imported = true;
      importButton.hidden = true;
      // A marker is optional; never remove or replace the original browser list.
      try { localStorage.setItem(migrationKey, '1'); } catch (_) {}
      message('Browser comparison imported to your account. The original browser list was retained.');
    } catch (error) { message(error.message); }
    finally { importButton.disabled = false; }
  });
  const pending = new URL(location.href);
  if (config.customer && pending.searchParams.has('ufm_compare_product')) {
    const entry = { id: pending.searchParams.get('ufm_compare_product'), variant: pending.searchParams.get('ufm_compare_variant'), url: pending.searchParams.get('ufm_compare_url') };
    (async () => {
      if (!accountMode) throw new Error(unavailable);
      if (accountMode) await account.ready;
      if (!window.confirm('Add this product to your comparison list?')) return;
      await add(entry);
      ['ufm_compare_product', 'ufm_compare_variant', 'ufm_compare_url'].forEach((name) => pending.searchParams.delete(name));
      history.replaceState(history.state, '', pending.pathname + pending.search + pending.hash);
    })().catch((error) => message(error.message));
  }
  window.addEventListener('storage', (event) => { if (event.key === key || event.key === null) { refresh(); if (page) render(); } });
  window.addEventListener('ufm:saved-lists-updated', () => { if (accountMode) { refresh(); if (page) render(); } });
  // Faceted search and recommendations replace cards without reloading the page.
  new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 &&
      (node.matches('[data-ufm-compare]') || node.querySelector('[data-ufm-compare]'))))) refresh();
  }).observe(document.body, { childList: true, subtree: true });
  refresh();
})();
